import type { StubFacts } from './stub';

/**
 * Draws the stub to a canvas and hands back a PNG.
 *
 * Drawn by hand rather than rasterising the DOM: that would need a library for
 * the one job, and cover art from AniList's CDN would taint the canvas and
 * make `toBlob` throw. The stub carries no covers, so nothing here is
 * cross-origin, and the file is the same on every browser.
 */
export const STUB_W = 1080;
export const STUB_H = 1350;

const DOT = '"DotGothic16", "MS Gothic", "Noto Sans JP", monospace';
const MIN = '"Shippori Mincho", "Yu Mincho", "Noto Serif JP", serif';
const ZEN = '"Zen Kaku Gothic New", "Hiragino Sans", "Noto Sans JP", system-ui, sans-serif';

const INK = '#14161A';
const SUB = '#5F5F5A';
const RULE = '#C4C0B2';
// The wordmark's two colours, inked for paper as on the stub (styles/brand.css).
const ANI = '#D4196A';
const METRO = '#B06400';

/** `sample` is any rider-supplied text, so its glyph subsets come down too. */
async function loadFonts(sample = '') {
  if (!('fonts' in document)) return;
  await Promise.all(
    [`32px ${DOT}`, `800 32px ${MIN}`, `900 32px ${ZEN}`].map((f) =>
      document.fonts.load(f, `次乗車券路線一本アニメトロ ABC123 ${sample}`).catch(() => []),
    ),
  );
}

function text(
  ctx: CanvasRenderingContext2D,
  s: string,
  x: number,
  y: number,
  font: string,
  color: string,
  opts: { align?: CanvasTextAlign; spacing?: number; max?: number } = {},
) {
  ctx.font = font;
  ctx.fillStyle = color;
  ctx.textAlign = opts.align ?? 'left';
  ctx.textBaseline = 'alphabetic';
  // letterSpacing is Chromium/Safari 17+; where it is missing the type is just tighter.
  (ctx as unknown as { letterSpacing: string }).letterSpacing = `${opts.spacing ?? 0}px`;
  ctx.fillText(s, x, y, opts.max);
}

/** Several colours on one baseline: the two-tone wordmark among plain text. */
function textRuns(
  ctx: CanvasRenderingContext2D,
  runs: [string, string][],
  x: number,
  y: number,
  font: string,
  opts: { align?: 'left' | 'center'; spacing?: number } = {},
) {
  ctx.font = font;
  (ctx as unknown as { letterSpacing: string }).letterSpacing = `${opts.spacing ?? 0}px`;
  const widths = runs.map(([s]) => ctx.measureText(s).width);
  let at = opts.align === 'center' ? x - widths.reduce((a, b) => a + b, 0) / 2 : x;
  runs.forEach(([s, color], i) => {
    text(ctx, s, at, y, font, color, { spacing: opts.spacing });
    at += widths[i] ?? 0;
  });
}

/** A small seeded generator, so a rider's ticket prints the same grain and specks every time. */
function rng(seed: number) {
  let a = seed >>> 0 || 1;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** `#FF2D3F` with an alpha, for gradients. */
function tint(hex: string, alpha: number): string {
  const n = Number.parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

/**
 * The ticket's outline: perforated along the top and bottom like the platform
 * tickets, with a round notch on each side where the stub tears off.
 */
function stockPath(x: number, y: number, w: number, h: number, tearY: number): Path2D {
  const p = new Path2D();
  const bite = 9;
  const pitch = 30;
  const count = Math.floor(w / pitch);
  const first = x + (w - (count - 1) * pitch) / 2;
  const notch = 26;
  p.moveTo(x, y);
  for (let k = 0; k < count; k++) {
    const cx = first + k * pitch;
    p.lineTo(cx - bite, y);
    p.arc(cx, y, bite, Math.PI, 0, true);
  }
  p.lineTo(x + w, y);
  p.lineTo(x + w, tearY - notch);
  p.arc(x + w, tearY, notch, -Math.PI / 2, Math.PI / 2, true);
  p.lineTo(x + w, y + h);
  for (let k = count - 1; k >= 0; k--) {
    const cx = first + k * pitch;
    p.lineTo(cx + bite, y + h);
    p.arc(cx, y + h, bite, 0, Math.PI, true);
  }
  p.lineTo(x, y + h);
  p.lineTo(x, tearY + notch);
  p.arc(x, tearY, notch, Math.PI / 2, -Math.PI / 2, true);
  p.closePath();
  return p;
}

/** Characters set round a circle, centred on the top (or, `below`, the bottom). */
function arcText(ctx: CanvasRenderingContext2D, s: string, r: number, font: string, below = false) {
  ctx.font = font;
  (ctx as unknown as { letterSpacing: string }).letterSpacing = '0px';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const chars = [...s];
  const step = 0.165;
  chars.forEach((c, i) => {
    const off = (i - (chars.length - 1) / 2) * step;
    const a = below ? Math.PI / 2 - off : -Math.PI / 2 + off;
    ctx.save();
    ctx.translate(Math.cos(a) * r, Math.sin(a) * r);
    ctx.rotate(below ? a - Math.PI / 2 : a + Math.PI / 2);
    ctx.fillText(c, 0, 0);
    ctx.restore();
  });
}

/** The 乗車 stamp, inked on its own canvas so the rubber's gaps can be knocked out of it. */
function stamp(color: string, issued: string, seed: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = 300;
  c.height = 300;
  const s = c.getContext('2d')!;
  s.translate(150, 150);
  s.strokeStyle = color;
  s.fillStyle = color;
  s.lineWidth = 10;
  s.beginPath();
  s.arc(0, 0, 128, 0, Math.PI * 2);
  s.stroke();
  s.lineWidth = 3.5;
  s.beginPath();
  s.arc(0, 0, 84, 0, Math.PI * 2);
  s.stroke();
  arcText(s, 'ANIMETRO', 104, `30px ${DOT}`);
  arcText(s, issued, 104, `28px ${DOT}`, true);
  s.font = `800 70px ${MIN}`;
  s.textAlign = 'center';
  s.textBaseline = 'middle';
  s.fillText('乗車', 0, 4);
  // uneven ink
  const r = rng(seed);
  s.globalCompositeOperation = 'destination-out';
  for (let i = 0; i < 520; i++) {
    s.globalAlpha = 0.25 + r() * 0.6;
    s.beginPath();
    s.arc((r() - 0.5) * 290, (r() - 0.5) * 290, 0.6 + r() * 2.4, 0, Math.PI * 2);
    s.fill();
  }
  return c;
}

export function drawStub(ctx: CanvasRenderingContext2D, f: StubFacts) {
  const accent = f.line?.color ?? '#FFB01F';
  // the band and glow keep the screen colour; anything printed on the paper is inked
  const ink = f.line?.ink ?? '#B06400';
  const seed = Number(f.serial) || 1;
  const r = rng(seed);

  // ---- the backdrop: night, a glow in the line's colour, manga focus lines
  const bg = ctx.createLinearGradient(0, 0, 0, STUB_H);
  bg.addColorStop(0, '#141A2B');
  bg.addColorStop(1, '#05070C');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, STUB_W, STUB_H);
  const glow = ctx.createRadialGradient(STUB_W / 2, STUB_H * 0.46, 60, STUB_W / 2, STUB_H * 0.5, 820);
  glow.addColorStop(0, tint(accent, 0.42));
  glow.addColorStop(0.55, tint(accent, 0.12));
  glow.addColorStop(1, tint(accent, 0));
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, STUB_W, STUB_H);

  ctx.save();
  ctx.translate(STUB_W / 2, STUB_H / 2);
  for (let i = 0; i < 150; i++) {
    const a = (i / 150) * Math.PI * 2 + r() * 0.03;
    const inner = 560 + r() * 160;
    const half = 0.002 + r() * 0.006;
    ctx.fillStyle = `rgba(255,255,255,${0.04 + r() * 0.07})`;
    ctx.beginPath();
    ctx.moveTo(Math.cos(a) * inner, Math.sin(a) * inner);
    ctx.lineTo(Math.cos(a - half) * 1000, Math.sin(a - half) * 1000);
    ctx.lineTo(Math.cos(a + half) * 1000, Math.sin(a + half) * 1000);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();

  // a few sparkles in the margins
  for (let i = 0; i < 14; i++) {
    const side = i % 2 ? STUB_W - 40 - r() * 60 : 40 + r() * 60;
    const sx = i < 4 ? 120 + r() * 840 : side;
    const sy = i < 4 ? (i % 2 ? STUB_H - 30 - r() * 30 : 30 + r() * 30) : 80 + r() * (STUB_H - 160);
    const sz = 3 + r() * 9;
    ctx.fillStyle = `rgba(255,255,255,${0.35 + r() * 0.5})`;
    ctx.beginPath();
    ctx.moveTo(sx, sy - sz);
    ctx.quadraticCurveTo(sx, sy, sx + sz, sy);
    ctx.quadraticCurveTo(sx, sy, sx, sy + sz);
    ctx.quadraticCurveTo(sx, sy, sx - sz, sy);
    ctx.quadraticCurveTo(sx, sy, sx, sy - sz);
    ctx.fill();
  }

  // ---- the stock, tilted a touch as if just put down
  const x = 112;
  const y = 92;
  const w = STUB_W - 224;
  const h = STUB_H - 184;
  const tearY = y + h - 236;
  ctx.save();
  ctx.translate(STUB_W / 2, STUB_H / 2);
  ctx.rotate((-2 * Math.PI) / 180);
  ctx.translate(-STUB_W / 2, -STUB_H / 2);

  const stock = stockPath(x, y, w, h, tearY);
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,.65)';
  ctx.shadowBlur = 70;
  ctx.shadowOffsetY = 30;
  ctx.fillStyle = '#F7F4EC';
  ctx.fill(stock);
  ctx.restore();

  ctx.save();
  ctx.clip(stock);
  const paper = ctx.createLinearGradient(0, y, 0, y + h);
  paper.addColorStop(0, '#FBF9F2');
  paper.addColorStop(1, '#F0EBDD');
  ctx.fillStyle = paper;
  ctx.fillRect(x, y, w, h);
  ctx.fillStyle = '#E9E3D3';
  ctx.fillRect(x, tearY, w, y + h - tearY);
  // grain
  for (let i = 0; i < 2600; i++) {
    ctx.fillStyle = r() < 0.5 ? 'rgba(80,60,30,.05)' : 'rgba(255,255,255,.35)';
    ctx.fillRect(x + r() * w, y + r() * h, 1 + r() * 1.5, 1 + r() * 1.5);
  }
  // the line's kanji as a watermark under the print
  ctx.globalAlpha = 0.06;
  text(ctx, f.line?.kanji.slice(0, 1) ?? '次', x + w - 30, tearY - 40, `800 360px ${MIN}`, accent, {
    align: 'right',
  });
  ctx.globalAlpha = 1;
  // the line's band, with the same colour along the stub's edge
  ctx.fillStyle = accent;
  ctx.fillRect(x, y, w, 26);
  ctx.fillRect(x, y + h - 12, w, 12);
  ctx.restore();

  const px = x + 52;
  const right = x + w - 52;
  textRuns(
    ctx,
    [
      ['アニ', ANI],
      ['メトロ', METRO],
      [' ・ ', SUB],
      ['ANI', ANI],
      ['METRO', METRO],
    ],
    px,
    y + 86,
    `26px ${DOT}`,
    { spacing: 3 },
  );
  text(ctx, '乗車券 ・ RIDER PASS', right, y + 86, `26px ${DOT}`, SUB, { align: 'right', spacing: 3 });
  ctx.fillStyle = INK;
  ctx.fillRect(px, y + 106, w - 104, 4);

  // the line, large
  text(ctx, f.line?.code ?? 'ANI', px - 6, y + 284, `180px ${DOT}`, ink, { spacing: -4 });
  text(ctx, f.line?.kanji ?? '次の一本', right, y + 214, `800 72px ${MIN}`, INK, { align: 'right', max: 330 });
  text(ctx, f.line?.lineEn ?? 'ANIMETRO', right, y + 266, `26px ${DOT}`, SUB, { align: 'right', spacing: 4 });

  // the rider
  text(ctx, `RIDER ・ ${f.source}`, px, y + 340, `24px ${DOT}`, SUB, { spacing: 5 });
  text(ctx, f.rider, px, y + 412, `900 72px ${ZEN}`, INK, { max: w - 330 });

  // the rider's network, as a route strip
  const net = f.network;
  const stripY = y + 514;
  const lines = net.length === 1 ? '1 LINE' : `${net.length} LINES`;
  text(ctx, net.length > 0 ? `路線 ・ ${lines}` : '路線 ・ YOUR LINES', px, stripY - 46, `22px ${DOT}`, SUB, {
    spacing: 5,
  });
  if (net.length > 0) {
    const gap = net.length > 1 ? (w - 104 - 40) / (net.length - 1) : 0;
    const at = (i: number) => px + 20 + i * gap;
    ctx.lineCap = 'round';
    net.forEach((l, i) => {
      if (i === net.length - 1) return;
      ctx.strokeStyle = l.color;
      ctx.lineWidth = 12;
      ctx.beginPath();
      ctx.moveTo(at(i), stripY);
      ctx.lineTo(at(i + 1), stripY);
      ctx.stroke();
    });
    net.forEach((l, i) => {
      ctx.fillStyle = '#FFFFFF';
      ctx.strokeStyle = l.color;
      ctx.lineWidth = 8;
      ctx.beginPath();
      ctx.arc(at(i), stripY, i === 0 ? 20 : 15, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      text(ctx, l.code, at(i), stripY + 52, `${net.length > 10 ? 19 : 22}px ${DOT}`, i === 0 ? INK : SUB, {
        align: 'center',
        spacing: net.length > 10 ? 0 : 2,
      });
    });
  } else {
    ctx.fillStyle = RULE;
    ctx.fillRect(px, stripY - 2, w - 104, 4);
  }

  // the facts, as the ticket grid
  const rows: [string, string][] = [
    ['HOME STATION', f.genre ? `${f.genre.toUpperCase()}${f.genreShare ? ` ・ ${f.genreShare}` : ''}` : '-'],
    ['OBSCURITY', f.obscurity ?? '-'],
    ['SCORING', f.grader ?? '-'],
    ['RIDDEN', `${f.titles} TITLES ・ ${f.hours} HRS`],
    ['HIDDEN GEM', f.gem ?? '-'],
    ['LEFT EARLY', f.dropRate ? `${f.dropRate} OF TITLES` : '-'],
  ];
  const gridY = y + 604;
  const colW = (w - 104) / 2;
  rows.forEach(([label, value], i) => {
    const cx = px + (i % 2) * colW;
    const cy = gridY + Math.floor(i / 2) * 98;
    ctx.fillStyle = RULE;
    ctx.fillRect(cx, cy, colW - 28, 3);
    text(ctx, label, cx, cy + 34, `20px ${DOT}`, SUB, { spacing: 5 });
    text(ctx, value, cx, cy + 74, `900 32px ${ZEN}`, INK, { max: colW - 40 });
  });

  // the tear
  ctx.strokeStyle = RULE;
  ctx.lineWidth = 4;
  ctx.setLineDash([16, 12]);
  ctx.beginPath();
  ctx.moveTo(x + 34, tearY);
  ctx.lineTo(x + w - 34, tearY);
  ctx.stroke();
  ctx.setLineDash([]);

  // the stub: where the rider goes next, and the barcode
  text(ctx, '次の一本 ・ NEXT STOP', px, tearY + 54, `22px ${DOT}`, SUB, { spacing: 5 });
  if (f.next) {
    ctx.fillStyle = ink;
    ctx.beginPath();
    ctx.moveTo(px, tearY + 78);
    ctx.lineTo(px + 22, tearY + 92);
    ctx.lineTo(px, tearY + 106);
    ctx.fill();
    // a long title steps down a size before it starts squeezing
    const room = w - 104 - 38;
    ctx.font = `900 38px ${ZEN}`;
    (ctx as unknown as { letterSpacing: string }).letterSpacing = '0px';
    const size = Math.max(28, Math.min(38, Math.floor((38 * room) / ctx.measureText(f.next).width)));
    text(ctx, f.next, px + 38, tearY + 106, `900 ${size}px ${ZEN}`, INK, { max: room });
  } else {
    text(ctx, 'THE CENTRAL STATION', px, tearY + 106, `900 38px ${ZEN}`, INK);
  }
  let bx = px;
  f.bars.forEach((n, i) => {
    const bw = n * 3;
    if (i % 2 === 0) {
      ctx.fillStyle = INK;
      ctx.fillRect(bx, tearY + 134, bw, 66);
    }
    bx += bw + 3;
  });
  text(ctx, `No. ${f.serial}`, right, tearY + 166, `36px ${DOT}`, INK, { align: 'right', spacing: 3 });
  text(ctx, `ISSUED ${f.issued}`, right, tearY + 200, `20px ${DOT}`, SUB, { align: 'right', spacing: 4 });

  // the stamp, beside the rider's name
  ctx.save();
  ctx.translate(right - 112, y + 372);
  ctx.rotate((-14 * Math.PI) / 180);
  ctx.globalAlpha = 0.8;
  ctx.globalCompositeOperation = 'multiply';
  ctx.drawImage(stamp(ink, f.issued, seed), -106, -106, 212, 212);
  ctx.restore();

  ctx.restore();
}

export async function renderStubPng(facts: StubFacts): Promise<Blob> {
  await loadFonts(`${facts.rider} ${facts.next ?? ''} ${facts.gem ?? ''} ${facts.line?.kanji ?? ''}`);
  const canvas = document.createElement('canvas');
  canvas.width = STUB_W;
  canvas.height = STUB_H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no 2d canvas');
  drawStub(ctx, facts);
  return await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png'),
  );
}

/**
 * Whether to hand the PNG to the OS share sheet: only on touch devices. Desktop
 * Chrome and Edge on Windows can share files too, but there the sheet lists
 * Paint and OneDrive rather than the apps people post from, so a computer
 * downloads instead.
 */
export function canShareFiles(): boolean {
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
  const touch = window.matchMedia?.('(hover: none) and (pointer: coarse)').matches ?? false;
  return touch && !!nav.canShare?.({ files: [new File([''], 'ticket.png', { type: 'image/png' })] });
}

/**
 * Share the PNG with the OS sheet where the browser allows files, else download it.
 * `text` rides along into whatever app the rider picks from the sheet.
 */
export async function shareOrSave(blob: Blob, filename: string, text?: string): Promise<'shared' | 'saved'> {
  const file = new File([blob], filename, { type: 'image/png' });
  if (canShareFiles()) {
    try {
      await navigator.share({ files: [file], title: 'My Animetro ticket', text });
      return 'shared';
    } catch (cause) {
      // The rider closing the sheet is not an error and should not trigger a download.
      if (cause instanceof DOMException && cause.name === 'AbortError') return 'shared';
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return 'saved';
}
